import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { BaseResourceManager } from '../BaseResourceManager';
import { SchoolDistrictField, ResoResource } from '../../system/reso';
import { useReso } from '../../contexts/ResoContext';
import { ResourceManagerOptions } from '../types';

// Field name mapping for SchoolDistrict
const FIELD_MAPPING: Record<string, string> = {
  [SchoolDistrictField.KEY]: 'SCHOOL_DISTRICT_KEY',
  [SchoolDistrictField.NAME]: 'SCHOOL_DISTRICT_NAME',
  [SchoolDistrictField.URL]: 'SCHOOL_DISTRICT_URL',
  [SchoolDistrictField.COUNTY]: 'SCHOOL_DISTRICT_COUNTY',
  [SchoolDistrictField.STATE]: 'SCHOOL_DISTRICT_STATE',
  [SchoolDistrictField.MODIFICATION_TIMESTAMP]:
    'SCHOOL_DISTRICT_MODIFICATION_TIMESTAMP',
};

export class SchoolDistrictResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[FIELD_MAPPING[SchoolDistrictField.KEY]];
  }

  getItemNumber(): number | undefined {
    return undefined; // School districts don't have an item number
  }

  getListingKeyField(): string | undefined {
    return undefined; // School districts are not directly related to listings
  }

  getListingKey(): string | undefined {
    return undefined; // School districts are not directly related to listings
  }

  // Utility methods for school district resources
  getName(): string | undefined {
    return this[FIELD_MAPPING[SchoolDistrictField.NAME]];
  }

  getUrl(): string | undefined {
    return this[FIELD_MAPPING[SchoolDistrictField.URL]];
  }

  getCounty(): string | undefined {
    return this[FIELD_MAPPING[SchoolDistrictField.COUNTY]];
  }

  getState(): string | undefined {
    return this[FIELD_MAPPING[SchoolDistrictField.STATE]];
  }
}

export class SchoolDistrictResourceManager extends BaseResourceManager<SchoolDistrictResource> {
  constructor(
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: ResourceManagerOptions = {},
  ) {
    super(
      ResoResource.SCHOOL_DISTRICT,
      executeQuery,
      fieldTranslator,
      taskManager,
      {
        ...options,
        useTranslation: false, // Disable translation
      },
    );
  }

  /**
   * Map field name using our custom mapping
   */
  protected mapFieldName(field: string): string {
    return FIELD_MAPPING[field] || field;
  }

  /**
   * Core mapping function from API data to SchoolDistrictResource
   */
  protected mapFromApiCore(apiData: any): SchoolDistrictResource {
    return new SchoolDistrictResource(apiData);
  }

  /**
   * Core mapping function from SchoolDistrictResource to API data
   */
  protected mapToApiCore(resource: SchoolDistrictResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Find school districts by name
   */
  public async findByName(name: string): Promise<SchoolDistrictResource[]> {
    return this.findListByField(SchoolDistrictField.NAME, name);
  }

  /**
   * Find school districts by county
   */
  public async findByCounty(county: string): Promise<SchoolDistrictResource[]> {
    return this.findListByField(SchoolDistrictField.COUNTY, county);
  }

  /**
   * Find school districts by state
   */
  public async findByState(state: string): Promise<SchoolDistrictResource[]> {
    return this.findListByField(SchoolDistrictField.STATE, state);
  }

  /**
   * Find school districts by state and county
   */
  public async findByStateAndCounty(
    state: string,
    county: string,
  ): Promise<SchoolDistrictResource[]> {
    return this.findListByFields({
      [SchoolDistrictField.STATE]: state,
      [SchoolDistrictField.COUNTY]: county,
    });
  }

  /**
   * Override findListByField to use our field mapping
   */
  public async findListByField(
    field: string,
    value: any,
  ): Promise<SchoolDistrictResource[]> {
    const mappedField = this.mapFieldName(field);
    return super.findListByField(mappedField, value);
  }

  /**
   * Override findByField to use our field mapping
   */
  public async findByField(
    field: string,
    value: any,
  ): Promise<SchoolDistrictResource | null> {
    const mappedField = this.mapFieldName(field);
    return super.findByField(mappedField, value);
  }
}

/**
 * Create a new SchoolDistrictResource instance with required fields
 */
export function createSchoolDistrictResource(
  data: Partial<Record<string, any>> = {},
): SchoolDistrictResource {
  const defaults = {};

  return new SchoolDistrictResource({
    ...defaults,
    ...data,
  });
}
